import { useState, useEffect } from 'react';
import { RefreshCw, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

export const PWAUpdateNotification = () => {
  const [showUpdate, setShowUpdate] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);

  useEffect(() => {
    // Check if service workers are supported
    if (!('serviceWorker' in navigator)) {
      return;
    }

    // Function to check for updates
    const checkForUpdates = () => {
      navigator.serviceWorker.ready.then((registration) => {
        // Check for updates every hour
        registration.update();
      });
    };

    // Check for updates on load and every hour
    checkForUpdates();
    const updateInterval = setInterval(checkForUpdates, 60 * 60 * 1000);

    // Listen for service worker updates
    const onUpdateFound = (registration: ServiceWorkerRegistration) => {
      const newWorker = registration.installing;
      
      if (newWorker) {
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            // New service worker is installed and waiting
            console.log('[PWA] New version available');
            setWaitingWorker(newWorker);
            setShowUpdate(true);
          }
        });
      }
    };

    // Listen for controlling service worker change
    const onControllerChange = () => {
      console.log('[PWA] Controller changed, reloading page');
      window.location.reload();
    };

    // Get the current registration
    navigator.serviceWorker.getRegistration().then((registration) => {
      if (registration) {
        // Check if there's already a waiting worker
        if (registration.waiting) {
          setWaitingWorker(registration.waiting);
          setShowUpdate(true);
        }

        // Listen for updates
        registration.addEventListener('updatefound', () => onUpdateFound(registration));
      }
    });

    // Listen for controller changes
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

    // Cleanup
    return () => {
      clearInterval(updateInterval);
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
    };
  }, []);

  const handleUpdate = () => {
    if (waitingWorker) {
      // Send message to service worker to skip waiting
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
      
      // The controllerchange event will trigger a reload
      setShowUpdate(false);
    }
  };

  const handleDismiss = () => {
    setShowUpdate(false);
  };

  if (!showUpdate) {
    return null;
  }

  return (
    <div className="fixed top-4 left-4 right-4 z-50 md:left-auto md:right-4 md:max-w-md animate-in slide-in-from-top-5 duration-500">
      <Card className="relative overflow-hidden border-primary/20 bg-card/95 backdrop-blur-xl shadow-glass-elevated">
        {/* Animated glow effect */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-primary/5 pointer-events-none animate-pulse" />
        
        <div className="relative p-4 space-y-3">
          {/* Icon and header */}
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center border border-primary/20">
              <Sparkles className="h-6 w-6 text-primary animate-pulse" />
            </div>
            
            <div className="flex-1 min-w-0">
              <h3 className="font-heading font-semibold text-foreground text-lg leading-tight">
                Update Available
              </h3>
              <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                A new version of Crystal HACCP is ready. Update now to get the latest features and improvements.
              </p>
            </div>
          </div>

          {/* Features highlight */}
          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground pl-15">
            <span className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
              Latest features
            </span>
            <span className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
              Bug fixes
            </span>
            <span className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
              Performance boost
            </span>
          </div>

          {/* Action buttons */}
          <div className="flex gap-2 pt-2">
            <Button
              onClick={handleUpdate}
              className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground shadow-glow"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Update Now
            </Button>
            <Button
              variant="outline"
              onClick={handleDismiss}
              className="border-border/50 hover:bg-accent/50"
            >
              Later
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};
